import i18n from '@web/i18n';

const locale = () => i18n.resolvedLanguage ?? 'en';

/** `10 Oct 2026, 14:05` in the given time zone (the org's, unless the user overrides it). */
export function formatDateTime(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale(), { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat(locale(), { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
  }
}

/** Full timestamp with seconds and UTC offset, for tooltips. */
/** Date and time with a 24-hour clock (e.g. "Oct 24, 2026, 21:31"), whatever the locale's habit. */
export function formatDateTime24(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '—';
  const opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short', hourCycle: 'h23' };
  try {
    return new Intl.DateTimeFormat(locale(), { ...opts, timeZone }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat(locale(), opts).format(new Date(iso));
  }
}

/** What an event says, as shown on charts and in the sequence log: a SmarDac Logger prefix such as "[Activity]" is dropped. */
export function eventText(text: string, source?: string | null): string {
  return source === 'logger' ? text.replace(/^\s*\[[^\]]*\]\s*/, '') : text;
}

export function formatDateTimeLong(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(locale(), { dateStyle: 'full', timeStyle: 'long', timeZone }).format(new Date(iso));
  } catch {
    return new Date(iso).toISOString();
  }
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['day', 86_400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

/** "5 seconds ago", "in 3 days". */
export function formatRelative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const diffSec = Math.round((new Date(iso).getTime() - now) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' });
  for (const [unit, size] of UNITS) {
    if (abs >= size || unit === 'second') return rtf.format(Math.round(diffSec / size), unit);
  }
  return '';
}

export function formatNumber(value: number, decimals = 0): string {
  return new Intl.NumberFormat(locale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
}

/** Triggers a browser download of text content (CSV exports built client-side). */
export function downloadText(filename: string, content: string, mime = 'text/csv;charset=utf-8', bom = true): void {
  // The byte order mark is for spreadsheets opening a CSV; JSON must not have one.
  const url = URL.createObjectURL(new Blob(bom ? [String.fromCharCode(0xfeff), content] : [content], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function toCsv(header: string[], rows: Array<Array<string | number | null | undefined>>): string {
  const cell = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? '' : String(v);
    const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
    return `"${safe.replaceAll('"', '""')}"`;
  };
  return [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
}
